// src/components/layout/Header.jsx

import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Menu, X, ShoppingCart, Phone } from 'lucide-react'
import { useCart } from '../../context/CartContext'
import { motion, AnimatePresence } from 'framer-motion'
import StepsBar from './StepsBar'

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Lab Tests', href: '/tests' },
  { name: 'How It Works', href: '/how-it-works' },
  { name: 'About', href: '/about' },
  { name: 'Blog', href: '/blog' },
  { name: 'FAQ', href: '/faq' },
  { name: 'Contact', href: '/contact' },
]

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { cartCount } = useCart()
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location])

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-soft' 
          : 'bg-white'
      }`}
    >
      {/* Top Bar */}
      <div className="bg-primary-500 text-white text-sm py-2 hidden md:block">
        <div className="container-custom text-center">
          <p>🔬 Fast, Private & Affordable Lab Testing - No Doctor Visit or Prescription Required</p>
        </div>
      </div>

      {/* Main Header */}
      <div className="container-custom">
        <nav className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/assets/logo.png" 
              alt="Labs.ChatWithDr" 
              className="h-14 w-auto group-hover:scale-105 transition-transform"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors relative py-2 ${
                    isActive 
                      ? 'text-primary-500' 
                      : 'text-gray-700 hover:text-primary-500'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.name}
                    {isActive && (
                      <motion.div
                        layoutId="activeNav"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          {/* Cart & Mobile Menu */}
          <div className="flex items-center gap-4">
            <Link 
              to="/checkout" 
              className="relative flex items-center gap-2 btn-primary !py-2.5 !px-4"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden sm:inline">Cart</span>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Mobile menu button */}
            <button
              type="button"
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
            </button>
          </div>
        </nav>
      </div>

      {/* Steps Bar - Shows on all pages */}
      <StepsBar />

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <div className="container-custom py-4 space-y-1">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `block py-3 px-4 rounded-xl text-base font-medium transition-colors ${
                      isActive 
                        ? 'bg-primary-50 text-primary-600' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              ))}
              
              {/* Mobile Steps Summary */}
              <div className="pt-4 border-t border-gray-100 mt-4">
                <div className="bg-primary-50 rounded-xl p-4">
                  <p className="text-sm font-semibold text-primary-800 mb-2">How It Works:</p>
                  <div className="space-y-2 text-sm text-primary-700">
                    <p>1. Order online - no prescription needed</p>
                    <p>2. Visit a nearby lab location</p>
                    <p>3. Get results in 24-72 hours</p>
                  </div>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-100 mt-4">
                <a 
                  href="tel:+19195882428" 
                  className="flex items-center gap-3 py-3 px-4 text-primary-500 font-medium"
                >
                  <Phone className="w-5 h-5" />
                  <span>919-588-2428</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}